import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { DayPicker } from 'react-day-picker'
import { DATETIME_FORMAT, LOCALE_DATE_FORMAT, MINUTES_STEP } from 'commons/js/constants'
import { parseMoment, toDate, mergeDate } from 'commons/js/util/dateConverter'
import { calculateToday } from 'commons/js/services/dateCalculator'
import Input from './Input'
import { Box, Flex, themeColor } from '@elparking/components'
import { es } from 'react-day-picker/locale'
import styled from 'styled-components'
import TimePicker from '../TimePicker'
import { transparentize } from 'polished'
import { LocaleMapping } from '@elparking/utils'

const grayColor = (props) => transparentize(0.6, themeColor('gray')(props))

const DateTimePickerContainer = styled(Flex)`
    position: absolute;
    background: white;
    width: 350px;
    z-index: 999;
    border-radius: 5px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 3.2em;

    border: 1px solid ${themeColor('lightGray40')};
    box-shadow: 2px 4px 4px 0 ${grayColor};

    /* Made with http://www.cssarrowplease.com/ */

    &:after,
    &:before {
        bottom: 100%;
        left: 1rem;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    }

    &:after {
        border-color: transparent;
        border-bottom-color: ${themeColor('white')};
        border-width: 10px;
        margin-left: -10px;
    }

    &:before {
        border-color: transparent;
        border-bottom-color: ${themeColor('lightGray40')};
        border-width: 11px;
        margin-left: -11px;
    }
`

    const TimePickerWrapper = styled.div`
    padding: 1em;
    border-top: 1px solid ${themeColor('lightGray40')};
`
const DesktopDateTimeSelect = ({
    value,
    format = LOCALE_DATE_FORMAT,
    timePickerProps = {},
    showTimePicker = true,
    onChange = () => {},
    minutesStep = MINUTES_STEP,
    showNextButton,
    onNext,
    placeholder,
    dayPickerProps,
    hideOnDayClick,
    showYearPicker,
    defaultIsOpen = false,
    onFocus,
}) => {
    const [selectedDay, setSelectedDay] = useState(value ? moment(value, DATETIME_FORMAT) : null)
    const [month, setMonth] = useState(value ? moment(value, DATETIME_FORMAT) : null)
    const [isOpen, setIsOpen] = useState(defaultIsOpen)
    const inputRef = useRef(null)
    useEffect(() => {
        const newSelectedDay = value ? parseMoment(value, DATETIME_FORMAT) : null
        if (value !== selectedDay) {
            setSelectedDay(newSelectedDay)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    useEffect(() => {
        setIsOpen(defaultIsOpen)
        if (defaultIsOpen) {
            inputRef.current.focus()
        }
    }, [defaultIsOpen])

    const handleDayChange = (date) => {
        const m = parseMoment(date)
        updateSelectedDay({
            year: m.year(),
            month: m.month(),
            date: m.date(),
        })
    }

    const handleTimeChange = (date) => {
        const m = parseMoment(date)
        updateSelectedDay({
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
        })
    }

    const updateSelectedDay = (partialDate) => {
        setSelectedDay((prevSelectedDay) => {
            const newSelectedDay = mergeDate(prevSelectedDay || toDate(calculateToday(minutesStep)), partialDate)
            onChange(newSelectedDay)
            return newSelectedDay
        })
        hideOnDayClick && setIsOpen(false)
    }

    const handleOnNext = () => {
        setIsOpen(false)
        onNext()
    }
    const openModalWithAccessibility = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsOpen(!isOpen)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          setIsOpen(false)
        }
    }
    return (
        <Box width='100%'>
            <Flex flexDirection='column'>
                <Input
                  key={isOpen}
                  ref={inputRef}
                  value={selectedDay}
                  format={format}
                  onKeyDown={openModalWithAccessibility}
                  onClick={() => setIsOpen(!isOpen)}
                  placeholder={placeholder}
                  onFocus={onFocus}
                />
                {isOpen && (
                    <DateTimePickerContainer height={showTimePicker ? '500px' : '400px'} onKeyDown={openModalWithAccessibility}>
                        <DayPicker
                          locale={LocaleMapping[dayPickerProps.locale] || es}
                          mode='single'
                          selected={selectedDay}
                          onSelect={handleDayChange}
                          month={month}
                          onMonthChange={setMonth}
                          disabled={dayPickerProps.disabledDays}
                          startMonth={dayPickerProps.fromMonth ? toDate(dayPickerProps.fromMonth) : null}
                          endMonth={dayPickerProps.toMonth ? toDate(dayPickerProps.toMonth) : null}
                          captionLayout={showYearPicker ? 'dropdown' : 'label'}
                        />
                        <hr />
                        {showTimePicker && <Box width='100%'>
                            <TimePickerWrapper>
                                <TimePicker
                                  {...timePickerProps}
                                  showNextButton={showNextButton}
                                  value={selectedDay}
                                  onNext={handleOnNext}
                                  onChange={handleTimeChange}
                                />
                            </TimePickerWrapper>
                        </Box>}
                    </DateTimePickerContainer>
                )}
            </Flex>
        </Box>
    )
}

DesktopDateTimeSelect.propTypes = {
    value: PropTypes.instanceOf(Date),
    format: PropTypes.string,
    dayPickerProps: PropTypes.object,
    timePickerProps: PropTypes.object,
    showTimePicker: PropTypes.bool,
    onChange: PropTypes.func,
    minutesStep: PropTypes.number,
    onNext: PropTypes.func,
    showNextButton: PropTypes.bool,
    placeholder: PropTypes.string,
    hideOnDayClick: PropTypes.bool,
    showYearPicker: PropTypes.bool,
    defaultIsOpen: PropTypes.bool,
    onFocus: PropTypes.func,
}

export default DesktopDateTimeSelect
